import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient, { API_BASE_URL } from '../lib/api';
import { getSessionId } from '../lib/session';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getSessionId);

  // Keep the shared API client's authorization state synchronized.
  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Get current user info
      getCurrentUser();
    } else {
      delete apiClient.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const getCurrentUser = async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Token might be invalid, remove it
      logout();
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await apiClient.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('auth_token', access_token);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password) => {
    setLoading(true);
    try {
      const response = await apiClient.post(`${API_BASE_URL}/api/auth/register`, {
        name,
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('auth_token', access_token);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await apiClient.post(`${API_BASE_URL}/api/auth/logout`);
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('auth_token');
      delete apiClient.defaults.headers.common['Authorization'];
    }
  };

  const value = {
    user,
    token,
    sessionId,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
